import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Card, Container, Row, Col, Spinner, Form, Badge, Button, Alert } from 'react-bootstrap';
import { NavSidebar } from './components/NavSidebar';
import { useAuth } from '../context/AuthContext';
import { useFilterContext } from '../context/SharedFilterContext';
import DatePicker from 'react-date-picker';
import moment from 'moment';
import DataTable from 'react-data-table-component';
import WorkOrders from '../models/WorkOrders';
import Company from '../models/Company';
import User from '../models/User';
import PropertyList from '../models/PropertyList';

import pLimit from 'p-limit';
import { FaCalendarAlt, FaFilter, FaFileExport, FaSync, FaSave } from 'react-icons/fa';
import {
  BrowserRouter as Router,
  useLocation, 
  useHistory
} from "react-router-dom";
import {MdArrowBack} from 'react-icons/md';

const CostAvoidanceScreen = () => {
  const { user } = useAuth();
  const history = useHistory();
  const [isLoading, setIsLoading] = useState(true);
  const [exportLoading, setExportLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  // Get property list state from SharedFilterContext
  const {
    inspectionViewMode: viewMode,
    setInspectionViewMode: setViewMode,
    selectedPropertyList,
    setSelectedPropertyList,
    availablePropertyLists,
    setAvailablePropertyLists
  } = useFilterContext();

  // Add new state for favorited properties functionality
  const [currentUserData, setCurrentUserData] = useState(null);
  const [hasFavorites, setHasFavorites] = useState(false);
  const [allWorkOrders, setAllWorkOrders] = useState([]); // Store all work orders
  const [filteredWorkOrders, setFilteredWorkOrders] = useState([]); // Store filtered work orders

  // Property list state variables (keeping hasPropertyLists local as it's UI-specific)
  const [hasPropertyLists, setHasPropertyLists] = useState(false);

  // State for date range
  const [startDate, setStartDate] = useState(moment().subtract(3, 'month').startOf('day').toDate());
  const [endDate, setEndDate] = useState(moment().startOf('day').toDate());

  // Temporary state for date range
  const [tempStartDate, setTempStartDate] = useState(startDate);
  const [tempEndDate, setTempEndDate] = useState(endDate);

  const [corrigoWorkOrders, setCorrigoWorkOrders] = useState([]);
  const [error, setError] = useState(null);
  const [paginationPerPage, setPaginationPerPage] = useState(100);
  
  // State for data assumptions - initialize to 0
  const [copelandDiscusCost, setCopelandDiscusCost] = useState(0);
  const [carlyleShcCost, setCarlyleShcCost] = useState(0);
  const [copelandScrollCost, setCopelandScrollCost] = useState(0);

  // Constants for chunking and concurrency
  const CHUNK_SIZE = 100;
  const MAX_CONCURRENCY = 6;

  const limit = pLimit(MAX_CONCURRENCY);

  // Add state for tracking updates
  const [isUpdating, setIsUpdating] = useState(false);
  const [updateMessage, setUpdateMessage] = useState(null);
  const [updatingAvoid, setUpdatingAvoid] = useState(new Set());

  // Memoize the fetchChunk function to prevent unnecessary recreations
  const fetchChunk = useCallback(async (ids) => {
    const searchList = [{ field: 'work_order_id', value: ids, comparison: 'IN' }];
    const { data } = await WorkOrders.filterBy(
      searchList, 'dt_created', 'desc', 10000, undefined, undefined, undefined, true
    );
    return data;
  }, []);

  // Add function to fetch current user data with fresh favorite_properties
  const fetchCurrentUserData = async () => {
    try {
      const freshUserData = await User.findByUUID(user.uuid);
      setCurrentUserData(freshUserData);
      
      // Check if user has favorites and parse JSON
      let favoriteUuids = [];
      if (freshUserData?.favorite_properties) {
        try {
          const parsedFavorites = JSON.parse(freshUserData.favorite_properties);
          favoriteUuids = Array.isArray(parsedFavorites) ? parsedFavorites : [];
        } catch (parseError) {
          console.warn('Error parsing favorite_properties:', parseError);
          favoriteUuids = [];
        }
      }
      
      const hasUserFavorites = favoriteUuids.length > 0;
      setHasFavorites(hasUserFavorites);
      
      return { freshUserData, favoriteUuids };
    } catch (error) {
      console.error('Error fetching user data:', error);
      setHasFavorites(false);
      return { freshUserData: null, favoriteUuids: [] };
    }
  };
  
  // Function to fetch available property lists for the company
  const fetchPropertyLists = async () => {
    try {
      if (!user?.company?.id) {
        setAvailablePropertyLists([]);
        setHasPropertyLists(false);
        return [];
      }
      
      const lists = await PropertyList.getPropertyListsByCompany(user.company.id);
      setAvailablePropertyLists(lists || []);
      setHasPropertyLists(lists && lists.length > 0);
      return lists || [];
    } catch (error) {
      console.error('Error fetching property lists:', error);
      setAvailablePropertyLists([]);
      setHasPropertyLists(false);
      return [];
    }
  };

  // Add function to filter work orders based on view mode
  const applyWorkOrderFiltering = async (workOrders, mode, explicitList = null) => {
    console.log(`\n========== [CostAvoidanceScreen] FILTERING - MODE: ${mode} ==========`);
    console.log(`Total work orders before filtering: ${workOrders.length}`);

    // Log sample of work order property_uuid values for debugging
    const sampleWorkOrders = workOrders.slice(0, 5);
    console.log("Sample work orders with property_uuid:");
    sampleWorkOrders.forEach((wo, index) => {
      console.log(`  WO ${index + 1}: work_order_id=${wo.work_order_id}, property_uuid=${wo.property_uuid}, property_number=${wo.property_number}`);
    });

    if (mode === 'favorited') {
      console.log("\n--- FAVORITED MODE ---");
      const { favoriteUuids } = await fetchCurrentUserData();

      console.log(`Number of favorited property UUIDs: ${favoriteUuids.length}`);
      if (favoriteUuids.length <= 10) {
        console.log("Favorited property UUIDs:", favoriteUuids);
      }

      if (favoriteUuids.length === 0) {
        console.log("No favorites found, returning empty array");
        setFilteredWorkOrders([]);
        return;
      }

      // Filter work orders where property_uuid is in user's favorites
      const favoritedWorkOrders = workOrders.filter(workOrder =>
        workOrder.property_uuid && favoriteUuids.includes(workOrder.property_uuid)
      );

      console.log(`Filtered ${favoritedWorkOrders.length} work orders for favorited properties`);
      setFilteredWorkOrders(favoritedWorkOrders);

    } else if (mode === 'propertyList') {
      console.log("\n--- PROPERTY LIST MODE ---");

      // Use the explicitly passed list if available, otherwise use the one from state
      const listToUse = explicitList || selectedPropertyList;

      if (!listToUse) {
        console.log("No property list selected, returning empty array");
        setFilteredWorkOrders([]);
        return;
      }

      console.log(`Selected property list: ${listToUse.name} (UUID: ${listToUse.uuid})`);

      // Get property UUIDs from the selected list
      let propertyUuids = [];
      if (listToUse.properties_list) {
        try {
          propertyUuids = JSON.parse(listToUse.properties_list);
          console.log(`Successfully parsed property list with ${propertyUuids.length} properties`);
          if (propertyUuids.length <= 10) {
            console.log("Property UUIDs in list:", propertyUuids);
          }
        } catch (e) {
          console.error('Error parsing properties_list:', e);
          propertyUuids = [];
        }
      }

      if (propertyUuids.length === 0) {
        console.log("Property list is empty, returning empty array");
        setFilteredWorkOrders([]);
        return;
      }

      // Filter work orders where property_uuid is in the property list
      const propertyListWorkOrders = workOrders.filter(workOrder =>
        workOrder.property_uuid && propertyUuids.includes(workOrder.property_uuid)
      );

      console.log(`Filtered ${propertyListWorkOrders.length} work orders for property list "${listToUse.name}"`);

      // Additional debugging: Check if any property list UUIDs match work order UUIDs
      const matchingUuids = propertyUuids.filter(uuid =>
        workOrders.some(wo => wo.property_uuid === uuid)
      );
      console.log(`Property UUIDs from list that have work orders: ${matchingUuids.length}`);

      setFilteredWorkOrders(propertyListWorkOrders);

    } else {
      console.log("\n--- ALL MODE ---");
      console.log(`Showing all ${workOrders.length} work orders`);
      setFilteredWorkOrders(workOrders);
    }

    console.log(`========== END FILTERING ==========\n`);
  };

  const fetchFilteredWorkOrders = useCallback(async () => {
    console.log("[CostAvoidanceScreen] Fetching filtered work orders");
    setIsLoading(true);
    setError(null);

    const startTime = Date.now();

    try {
      // Fetch work order IDs by date range
      const workOrderIds = await WorkOrders.getWorkOrderIdsByDateRange(
        moment(startDate).format('YYYY-MM-DD HH:mm:ss'),
        moment(endDate).add(1, 'day').format('YYYY-MM-DD HH:mm:ss')
      );

      console.log("Work Order IDs by Date Range:", workOrderIds);

      if (workOrderIds.length === 0) {
        setAllWorkOrders([]);
        setFilteredWorkOrders([]);
        setIsLoading(false);
        return;
      }

      // Break work order IDs into chunks of CHUNK_SIZE
      const chunks = [];
      for (let i = 0; i < workOrderIds.length; i += CHUNK_SIZE) {
        chunks.push(workOrderIds.slice(i, i + CHUNK_SIZE));
      }

      // Fetch work orders for each chunk in parallel, limited by p-limit
      let allWorkOrders = [];
      await Promise.all(chunks.map(chunk => 
        limit(() => fetchChunk(chunk).then(res => allWorkOrders.push(...res)))
      ));

      // Apply existing filtering logic
      allWorkOrders = allWorkOrders.filter(wo => 
        wo.status_id === 'Completed' && 
        wo.type_category !== 'PMRM' && 
        wo.source !== 'workzone_area_match_historical_context' &&
        wo.avoid !== 1  // Filter out work orders with avoid = 1
      );

      // Count and log the number of work orders with 'source' as "workzone_area_match_historical_context"
      const historicalContextCount = allWorkOrders.filter(wo => wo.source === 'workzone_area_match_historical_context').length;
      console.log(`Number of work orders with source 'workzone_area_match_historical_context': ${historicalContextCount}`);

      // Explicitly sort work orders by dt_created in descending order
      allWorkOrders.sort((a, b) => new Date(b.dt_created) - new Date(a.dt_created));
      console.log("This is the sorted work orders", allWorkOrders);
      
      // Store all work orders
      setAllWorkOrders(allWorkOrders);
      
      // Apply filtering based on current view mode
      await applyWorkOrderFiltering(allWorkOrders, viewMode);
      
    } catch (err) {
      console.error("[CostAvoidanceScreen] Error fetching work orders:", err);
      setError(err.message || "Failed to fetch work orders");
    } finally {
      setIsLoading(false);
      const endTime = Date.now();
      const durationInSeconds = (endTime - startTime) / 1000;
      console.log(`[CostAvoidanceScreen] Time taken to fetch all work orders: ${durationInSeconds} seconds`);
    }
  }, [fetchChunk, startDate, endDate, viewMode]);

  // Add function to handle view mode toggle
  const handleViewModeToggle = async (mode, propertyList = null) => {
    setViewMode(mode);

    // Clear property list when switching away from propertyList mode
    if (mode !== 'propertyList') {
      setSelectedPropertyList(null);
    } else if (propertyList) {
      // If a property list is provided, set it in state
      setSelectedPropertyList(propertyList);
    }

    // Apply filtering to existing work orders, passing the list if provided
    await applyWorkOrderFiltering(allWorkOrders, mode, propertyList);
  };

  // Fetch user data on component mount
  useEffect(() => {
    if (user?.uuid) {
      fetchCurrentUserData();
    }
  }, [user?.uuid]);
  
  // Fetch property lists when component mounts
  useEffect(() => {
    if (user?.company?.id) {
      fetchPropertyLists();
    }
  }, [user?.company?.id]);

  // Fetch work orders when the component mounts or date range changes
  useEffect(() => {
    fetchFilteredWorkOrders();
  }, [fetchFilteredWorkOrders]);

  // Update corrigoWorkOrders when filteredWorkOrders changes
  useEffect(() => {
    setCorrigoWorkOrders(filteredWorkOrders);
  }, [filteredWorkOrders]);

  const applyDateRange = () => {
    setStartDate(tempStartDate);
    setEndDate(tempEndDate);
  };

  // Function to decode data assumptions from JSON
  const decodeDataAssumptions = (dataAssumptionsStr) => {
    try {
      return JSON.parse(dataAssumptionsStr);
    } catch (error) {
      console.error("Error decoding data assumptions:", error);
      return {
        copelandDiscusCost: 0,
        carlyleShcCost: 0,
        copelandScrollCost: 0
      };
    }
  };

  // Function to encode data assumptions to JSON
  const encodeDataAssumptions = () => {
    return JSON.stringify({
      copelandDiscusCost,
      carlyleShcCost,
      copelandScrollCost
    });
  };

  // Fetch company data assumptions
  useEffect(() => {
    const fetchCompanyDataAssumptions = async () => {
      try {
        const companyArray = await Company.searchByCompanyUuid(user?.company?.uuid);
        const company = companyArray?.length > 0 ? companyArray[0] : null;
        
        if (company && company?.data_assumptions) {
          const dataAssumptions = decodeDataAssumptions(company?.data_assumptions);
          setCopelandDiscusCost(dataAssumptions.copelandDiscusCost || 0);
          setCarlyleShcCost(dataAssumptions.carlyleShcCost || 0);
          setCopelandScrollCost(dataAssumptions.copelandScrollCost || 0);
        }
      } catch (error) {
        console.error("Error fetching company data assumptions:", error);
      }
    };

    if (user?.company?.uuid) {
      fetchCompanyDataAssumptions();
    }
  }, [user?.company?.uuid]);

  // Save data assumptions
  const handleSaveDataAssumptions = async () => {
    setIsSaving(true);
    setUpdateMessage(null);
    
    try {
      const encodedDataAssumptions = encodeDataAssumptions();
      console.log("encodedData", encodedDataAssumptions);
      const result = await Company.update({
        uuid: user.company.uuid,
        data_assumptions: encodedDataAssumptions
      });
      
      setUpdateMessage({ type: 'success', text: 'Data assumptions saved successfully' });
    } catch (error) {
      console.error("Error saving data assumptions:", error);
      setUpdateMessage({ type: 'danger', text: `Error: ${error.message}` });
    } finally {
      setIsSaving(false);
      // Clear message after 3 seconds
      setTimeout(() => setUpdateMessage(null), 3000);
    }
  };

  const handleExportCSV = () => {
    setExportLoading(true);
    
    // Get headers from costAvoidanceColumns
    const headers = costAvoidanceColumns.map(column => {
      // Handle JSX column names
      if (typeof column.name === 'object') {
        return 'Positive Validation'; // Special case for the JSX header
      }
      return column.name;
    });
    
    // Create CSV rows using the same data and formatting as displayed in the table
    const rows = corrigoWorkOrders.map(row => {
      return costAvoidanceColumns.map(column => {
        // Handle different data formats based on column
        if (column.format && typeof column.format === 'function') {
          // Use the format function to get formatted value
          return `"${column.format(row).replace(/"/g, '""')}"`;
        } else if (column.selector === (row => row.validation)) {
          // Get validation value for Positive Validation column
          return `"${row.validation || ''}"`;
        } else if (column.name === 'Asset') {
          // Map asset IDs to names
          const assetMap = {
            48: 'Copeland Discus',
            49: 'Carlyle SHC',
            23: 'Copeland Scroll'
          };
          return `"${row.asset ? assetMap[row.asset] || '' : ''}"`;
        } else if (column.selector && typeof column.selector === 'function') {
          // Use selector function to get value
          const value = column.selector(row);
          return value !== undefined && value !== null ? `"${String(value).replace(/"/g, '""')}"` : '""';
        }
        // Default to empty string if no value
        return '""';
      }).join(',');
    });
    
    // Combine headers and rows
    const csvContent = [
      headers.join(','),
      ...rows
    ].join('\n');
    
    // Create download link
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `cost-avoidance-analysis-${moment().format('YYYY-MM-DD')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    setTimeout(() => setExportLoading(false), 1000);
  };

  // New function to handle validation changes
  const handleValidationChange = async (workOrderId, newValue) => {
    setIsUpdating(true);
    try {
      const success = await WorkOrders.updateWorkOrderVal(workOrderId, newValue);
      if (success) {
        // Update local state for immediate UI update
        setCorrigoWorkOrders(prevOrders => 
          prevOrders.map(wo => 
            wo.work_order_id === workOrderId ? {...wo, validation: newValue} : wo
          )
        );
        setUpdateMessage({ type: 'success', text: 'Validation updated successfully' });
      } else {
        setUpdateMessage({ type: 'danger', text: 'Failed to update validation' });
      }
    } catch (err) {
      console.error("Error updating validation:", err);
      setUpdateMessage({ type: 'danger', text: `Error: ${err.message}` });
    } finally {
      setIsUpdating(false);
      // Clear message after 3 seconds
      setTimeout(() => setUpdateMessage(null), 3000);
    }
  };

  // New function to handle asset changes
  const handleAssetChange = async (workOrderId, newValue) => {
    setIsUpdating(true);
    try {
      const success = await WorkOrders.updateWorkOrderAsset(workOrderId, newValue);
      if (success) {
        // Update local state for immediate UI update
        setCorrigoWorkOrders(prevOrders => 
          prevOrders.map(wo => 
            wo.work_order_id === workOrderId ? {...wo, asset: newValue} : wo
          )
        );
        setUpdateMessage({ type: 'success', text: 'Asset updated successfully' });
      } else {
        setUpdateMessage({ type: 'danger', text: 'Failed to update asset' });
      }
    } catch (err) {
      console.error("Error updating asset:", err);
      setUpdateMessage({ type: 'danger', text: `Error: ${err.message}` });
    } finally {
      setIsUpdating(false);
      // Clear message after 3 seconds
      setTimeout(() => setUpdateMessage(null), 3000);
    }
  };

  // Add function to handle avoid button click
  const handleAvoidClick = async (workOrderId) => {
    console.log(`[CostAvoidanceScreen] Setting avoid to 1 for work order ${workOrderId}`);
    
    // Add to updating set to show loading state
    setUpdatingAvoid(prev => new Set(prev).add(workOrderId));
    
    try {
      const success = await WorkOrders.updateWorkOrderAvoid(workOrderId, 1);
      if (success) {
        // Remove the work order from local state since it should be filtered out
        setCorrigoWorkOrders(prev => 
          prev.filter(wo => wo.work_order_id !== workOrderId)
        );
        setUpdateMessage({ type: 'success', text: 'Work order removed from cost avoidance analysis' });
      } else {
        setUpdateMessage({ type: 'danger', text: 'Failed to update work order' });
      }
    } catch (err) {
      console.error("Error updating avoid value:", err);
      setUpdateMessage({ type: 'danger', text: `Error: ${err.message}` });
    } finally {
      setUpdatingAvoid(prev => {
        const newSet = new Set(prev);
        newSet.delete(workOrderId);
        return newSet;
      });
      // Clear message after 3 seconds
      setTimeout(() => setUpdateMessage(null), 3000);
    }
  };

  const costAvoidanceColumns = useMemo(() => [
    // Add the avoid button column first
    {
      name: 'Remove',
      width: '80px',
      id: 'avoid_action',
      cell: row => {
        const isUpdating = updatingAvoid.has(row.work_order_id);
        
        return (
          <button
            onClick={() => handleAvoidClick(row.work_order_id)}
            disabled={isUpdating}
            style={{
              backgroundColor: '#dc3545', // Red button
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '4px 8px',
              fontSize: '12px',
              cursor: isUpdating ? 'not-allowed' : 'pointer',
              opacity: isUpdating ? 0.6 : 1,
              minWidth: '70px'
            }}
            title="Remove this work order from cost avoidance analysis"
          >
            {isUpdating ? '...' : 'Remove'}
          </button>
        );
      }
    },
    { 
      name: (
        <div style={{ lineHeight: '1.2', textAlign: 'center' }}>
          Positive<br />Validation
        </div>
      ), 
      selector: row => row.validation, 
      sortable: true, 
      width: '100px',
      cell: row => {
        const validationOptions = [
          { value: 1, label: '1', variant: 'success' },
          { value: 2, label: '2', variant: 'danger' },
          { value: 3, label: '3', variant: 'warning' }
        ];
        
        return (
          <Form.Control
            as="select"
            size="sm"
            value={row.validation || ''}
            onChange={(e) => handleValidationChange(row.work_order_id, parseInt(e.target.value) || null)}
            disabled={isUpdating}
            style={{ maxWidth: '100px' }}
          >
            <option value="">Select...</option>
            {validationOptions.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Control>
        );
      }
    },
    { 
      name: 'Asset', 
      selector: row => row.asset, 
      sortable: true, 
      width: '140px',
      cell: row => {
        const assetOptions = [
          { value: 48, label: 'Copeland Discus' },
          { value: 49, label: 'Carlyle SHC' },
          { value: 23, label: 'Copeland Scroll' }
        ];
        
        return (
          <Form.Control
            as="select"
            size="sm"
            value={row.asset || ''}
            onChange={(e) => handleAssetChange(row.work_order_id, parseInt(e.target.value) || null)}
            disabled={isUpdating}
            style={{ 
              maxWidth: '135px',
              whiteSpace: 'normal',
              overflow: 'hidden',
              height: '45px'
            }}
          >
            <option value="">Select...</option>
            {assetOptions.map(option => (
              <option key={option.value} value={option.value} style={{ whiteSpace: 'normal' }}>
                {option.label}
              </option>
            ))}
          </Form.Control>
        );
      }
    },
    { 
      name: 'DT Created', 
      selector: row => row.dt_created, 
      sortable: true, 
      width: '130px',
      format: row => moment(row.dt_created).format('MMM DD, YYYY')
    },
    { name: 'Number', selector: row => row.number, sortable: true, width: '140px' },
    { 
      name: 'DST Equipment', 
      selector: row => row.root_comp, 
      sortable: true, 
      width: '200px',
      cell: row => (
        <div
          title={row.root_comp}
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            lineHeight: '1.3',
            maxHeight: '3.9em',
            wordBreak: 'break-word'
          }}
        >
          {row.root_comp}
        </div>
      )
    },
    { 
      name: 'Task Refinement',
      selector: row => row.task_refinement,
      sortable: true,
      width: '525px',
      cell: row => (
        <div
          style={{
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            lineHeight: '1.3',
            padding: '8px 4px'
          }}
        >
          {row.task_refinement}
        </div>
      )
    },
    { name: 'Status', selector: row => row.status_id, sortable: true, width: '100px' },
    { 
      name: 'Completion Note Text', 
      selector: row => row.completion_note_text, 
      sortable: true, 
      width: '450px',
      cell: row => (
        <div
          style={{
            whiteSpace: 'normal',
            wordBreak: 'break-word',
            lineHeight: '1.3',
            padding: '8px 4px'
          }}
        >
          {row.completion_note_text || 'N/A'}
        </div>
      )
    },
    { name: 'Property', selector: row => row.property_number, sortable: true, width: '130px' },

    { name: 'Assignee', selector: row => row.assignee_screen_name, sortable: true, width: '150px' },
    { 
      name: 'DT Due', 
      selector: row => row.dt_due, 
      sortable: true, 
      width: '130px',
      format: row => row.dt_due ? moment(row.dt_due).format('MMM DD, YYYY') : 'N/A'
    },
    { name: 'Type Category', selector: row => row.type_category, sortable: true, width: '150px' },
    { name: 'Subtype Name', selector: row => row.subtype_name, sortable: true, width: '150px' },
    { 
      name: 'Costs Total', 
      selector: row => row.costs_total_value, 
      sortable: true, 
      width: '150px',
      right: true,
      format: row => row.costs_total_value ? `$${parseFloat(row.costs_total_value).toFixed(2)}` : '$0.00'
    },
    // { 
    //   name: 'Expenses Total', 
    //   selector: row => row.expenses_total_value, 
    //   sortable: true, 
    //   width: '170px',
    //   right: true,
    //   format: row => row.expenses_total_value ? `$${parseFloat(row.expenses_total_value).toFixed(2)}` : '$0.00'
    // },
    // { 
    //   name: 'Service Fees Total', 
    //   selector: row => row.service_fees_total_value, 
    //   sortable: true, 
    //   width: '170px',
    //   right: true,
    //   format: row => row.service_fees_total_value ? `$${parseFloat(row.service_fees_total_value).toFixed(2)}` : '$0.00'
    // },
    // { 
    //   name: 'Total Spot Purchase Cost', 
    //   selector: row => row.total_spot_cost || 0, 
    //   sortable: true, 
    //   width: '230px',
    //   right: true,
    //   format: row => `$${(row.total_spot_cost || 0).toFixed(2)}`
    // },
    { 
      name: "Opportunity $'s", 
      selector: row => {
        if (!row.asset) return 0;
        if (row.asset === 48) return copelandDiscusCost;
        if (row.asset === 49) return carlyleShcCost;
        if (row.asset === 23) return copelandScrollCost;
        return 0;
      }, 
      sortable: true, 
      width: '160px',
      right: true,
      format: row => {
        if (!row.asset) return '$0.00';
        let value = 0;
        if (row.asset === 48) value = copelandDiscusCost;
        if (row.asset === 49) value = carlyleShcCost;
        if (row.asset === 23) value = copelandScrollCost;
        return `$${value.toFixed(2)}`;
      }
    },
    { 
      name: 'Cost Avoided (Confirmed)', 
      selector: row => {
        if (row.validation !== 1) return 0;
        
        if (!row.asset) return 0;
        if (row.asset === 48) return copelandDiscusCost;
        if (row.asset === 49) return carlyleShcCost;
        if (row.asset === 23) return copelandScrollCost;
        return 0;
      }, 
      sortable: true, 
      width: '240px',
      right: true,
      format: row => {
        if (row.validation !== 1) return '$0.00';
        
        let value = 0;
        if (row.asset === 48) value = copelandDiscusCost;
        if (row.asset === 49) value = carlyleShcCost;
        if (row.asset === 23) value = copelandScrollCost;
        return `$${value.toFixed(2)}`;
      }
    },
    // { 
    //   name: 'Cost Avoided (Confirmed + Possible)', 
    //   selector: row => {
    //     if (row.validation !== 1 && row.validation !== 3) return 0;
        
    //     if (!row.asset) return 0;
    //     if (row.asset === 1) return copelandDiscusCost;
    //     if (row.asset === 2) return carlyleShcCost;
    //     if (row.asset === 3) return copelandScrollCost;
    //     return 0;
    //   }, 
    //   sortable: true, 
    //   width: '300px',
    //   right: true,
    //   format: row => {
    //     if (row.validation !== 1 && row.validation !== 3) return '$0.00';
        
    //     let value = 0;
    //     if (row.asset === 1) value = copelandDiscusCost;
    //     if (row.asset === 2) value = carlyleShcCost;
    //     if (row.asset === 3) value = copelandScrollCost;
    //     return `$${value.toFixed(2)}`;
    //   }
    // },
    { 
      name: 'Cost Incurred', 
      selector: row => parseFloat(row.costs_total_value || 0), 
      sortable: true, 
      width: '140px',
      right: true,
      format: row => `$${parseFloat(row.costs_total_value || 0).toFixed(2)}`
    },
    { 
      name: 'Net Cost Avoided (Confirmed)', 
      selector: row => {
        const costIncurred = parseFloat(row.costs_total_value || 0);
        
        // Get the Cost Avoided (Confirmed) value
        let costAvoidedConfirmed = 0;
        if (row.validation === 1 && row.asset) {
          if (row.asset === 48) costAvoidedConfirmed = copelandDiscusCost;
          if (row.asset === 49) costAvoidedConfirmed = carlyleShcCost;
          if (row.asset === 23) costAvoidedConfirmed = copelandScrollCost;
        }
        
        return costAvoidedConfirmed - costIncurred;
      }, 
      sortable: true, 
      width: '300px',
      right: true,
      format: row => {
        const costIncurred = parseFloat(row.costs_total_value || 0);
        
        // Get the Cost Avoided (Confirmed) value
        let costAvoidedConfirmed = 0;
        if (row.validation === 1 && row.asset) {
          if (row.asset === 48) costAvoidedConfirmed = copelandDiscusCost;
          if (row.asset === 49) costAvoidedConfirmed = carlyleShcCost;
          if (row.asset === 23) costAvoidedConfirmed = copelandScrollCost;
        }
        
        return `$${(costAvoidedConfirmed - costIncurred).toFixed(2)}`;
      }
    },
    // { 
    //   name: 'Net Cost Avoided (Confirmed + Possible)', 
    //   selector: row => {
    //     const costIncurred = parseFloat(row.costs_total_value || 0);
        
    //     // Get the Cost Avoided (Confirmed + Possible) value
    //     let costAvoidedConfirmedPossible = 0;
    //     if ((row.validation === 1 || row.validation === 3) && row.asset) {
    //       if (row.asset === 1) costAvoidedConfirmedPossible = copelandDiscusCost;
    //       if (row.asset === 2) costAvoidedConfirmedPossible = carlyleShcCost;
    //       if (row.asset === 3) costAvoidedConfirmedPossible = copelandScrollCost;
    //     }
        
    //     return costAvoidedConfirmedPossible - costIncurred;
    //   }, 
    //   sortable: true, 
    //   width: '330px',
    //   right: true,
    //   format: row => {
    //     const costIncurred = parseFloat(row.costs_total_value || 0);
        
    //     // Get the Cost Avoided (Confirmed + Possible) value
    //     let costAvoidedConfirmedPossible = 0;
    //     if ((row.validation === 1 || row.validation === 3) && row.asset) {
    //       if (row.asset === 1) costAvoidedConfirmedPossible = copelandDiscusCost;
    //       if (row.asset === 2) costAvoidedConfirmedPossible = carlyleShcCost;
    //       if (row.asset === 3) costAvoidedConfirmedPossible = copelandScrollCost;
    //     }
        
    //     return `$${(costAvoidedConfirmedPossible - costIncurred).toFixed(2)}`;
    //   }
    // },
  ], [copelandDiscusCost, carlyleShcCost, copelandScrollCost, isUpdating, updatingAvoid, handleValidationChange, handleAssetChange, handleAvoidClick]);

  // Custom styles for DataTable
  const tableCustomStyles = {
    header: {
      style: {
        minHeight: '56px',
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
      },
    },
    headRow: {
      style: {
        borderTopLeftRadius: '8px',
        borderTopRightRadius: '8px',
        backgroundColor: '#f0f4f8',
        borderBottom: '1px solid #dfe3e8',
      },
    },
    headCells: {
      style: {
        fontWeight: '600',
        color: '#212b36',
        fontSize: '14px',
        padding: '16px',
      },
    },
    cells: {
      style: {
        padding: '12px 16px',
        fontSize: '13px',
      },
    },
    pagination: {
      style: {
        borderBottomLeftRadius: '8px',
        borderBottomRightRadius: '8px',
        backgroundColor: '#f8fafc',
      },
      pageButtonsStyle: {
        borderRadius: '4px',
        height: '32px',
        width: '32px',
      },
    },
  };

  const goBack = () => {
    history.push(`/migration`);
  }

  return (
    <div className="d-flex flex-row justify-content-start align-items-start" style={{ width: '100%', height: '100vh' }}>
      <NavSidebar />
      <Container fluid style={{ padding: '20px', marginTop: '20px', backgroundColor: '#f8f9fa', borderRadius: '12px', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)', overflow: 'auto', marginLeft: '20px', marginRight: '20px' }}>
        
        {/* Go Back Button */}
        <div className={`d-flex flex-row flex-no-wrap justify-start align-items-center mb-3`}>
          <Button variant="link" onClick={() => {
            goBack();
          }}>
            <MdArrowBack />
          </Button>
          <div className="font-weight-bold">{'Go Back'}</div>
        </div>

        <div className="d-flex justify-content-between align-items-center mb-4">
          <h2 style={{ fontSize: '26px', fontWeight: 'bold', color: '#2c3e50', margin: 0 }}>Cost Avoidance Calculator</h2>
          
          <div className="d-flex">
            {/* Property Group Dropdown - Only show when groups are available */}
            {hasPropertyLists && availablePropertyLists.length > 0 && (
              <div className="d-flex flex-column mr-3">
                <span style={{ fontSize: '14px', fontWeight: '500', color: '#495057', marginBottom: '4px' }}>
                  Property Groups
                </span>
                <select
                  className="form-select"
                  value={selectedPropertyList?.uuid || ''}
                  onChange={(e) => {
                    const list = availablePropertyLists.find(l => l.uuid === e.target.value);
                    // Pass the list directly to handleViewModeToggle to avoid timing issues
                    if (list) {
                      handleViewModeToggle('propertyList', list);
                    } else {
                      setSelectedPropertyList(null);
                    }
                  }}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid #ced4da',
                    borderRadius: '4px',
                    fontSize: '14px',
                    backgroundColor: '#fff',
                    color: '#495057',
                    minWidth: '200px'
                  }}
                >
                  <option value="">Select Property Group...</option>
                  {availablePropertyLists.map(list => (
                    <option key={list.uuid} value={list.uuid}>
                      {list.name}
                    </option>
                  ))}
                </select>
              </div>
            )}
            
            {/* View Mode Toggle - Three buttons */}
            <div className="d-flex flex-row mr-3" style={{ alignItems: 'center' }}>
              <button
                onClick={() => handleViewModeToggle('favorited')}
                style={{
                  padding: '8px 16px',
                  backgroundColor: viewMode === 'favorited' ? '#0056b3' : '#f8f9fa',
                  color: viewMode === 'favorited' ? '#fff' : '#6c757d',
                  border: '1px solid #dee2e6',
                  borderRadius: '4px 0 0 4px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.3s',
                  opacity: 1
                }}
              >
                Favorited Properties ({hasFavorites ? (currentUserData?.favorite_properties ? 
                  (() => {
                    try {
                      return JSON.parse(currentUserData.favorite_properties).length || 0;
                    } catch {
                      return 0;
                    }
                  })() : 0) : 0})
              </button>
              
              {/* Property Group Button - Only show when a group is selected */}
              {selectedPropertyList && (
                <button
                  onClick={() => handleViewModeToggle('propertyList')}
                  style={{
                    padding: '8px 16px',
                    backgroundColor: viewMode === 'propertyList' ? '#28a745' : '#f8f9fa',
                    color: viewMode === 'propertyList' ? '#fff' : '#6c757d',
                    border: '1px solid #dee2e6',
                    borderRadius: '0',
                    borderLeft: 'none',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500',
                    transition: 'all 0.3s'
                  }}
                >
                  List ({selectedPropertyList?.properties_list ? 
                    (() => {
                      try {
                        return JSON.parse(selectedPropertyList.properties_list).length;
                      } catch {
                        return '?';
                      }
                    })() : '?'})
                </button>
              )}
              
              <button
                onClick={() => handleViewModeToggle('all')}
                style={{
                  padding: '8px 16px',
                  backgroundColor: viewMode === 'all' ? '#0056b3' : '#f8f9fa',
                  color: viewMode === 'all' ? '#fff' : '#6c757d',
                  border: '1px solid #dee2e6',
                  borderRadius: selectedPropertyList ? '0 4px 4px 0' : '0 4px 4px 0',
                  borderLeft: 'none',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '500',
                  transition: 'all 0.3s'
                }}
              >
                All Work Orders
              </button>
            </div>

            <Button 
              variant="outline-primary" 
              className="mr-2" 
              onClick={fetchFilteredWorkOrders}
              disabled={isLoading}
            >
              <FaSync className={isLoading ? 'fa-spin mr-1' : 'mr-1'} /> Refresh
            </Button>
            
            <Button 
              variant="outline-success" 
              onClick={handleExportCSV}
              disabled={isLoading || exportLoading || corrigoWorkOrders.length === 0}
            >
              <FaFileExport className="mr-1" /> {exportLoading ? 'Exporting...' : 'Export CSV'}
            </Button>
          </div>
        </div>

        {/* Favorited Properties Information */}
        {viewMode === 'favorited' && (
          <div className="d-flex justify-content-center mb-2">
            <div style={{
              padding: '8px 16px',
              backgroundColor: '#e7f3ff',
              border: '1px solid #b3d9ff',
              borderRadius: '4px',
              color: '#004085',
              fontSize: '14px'
            }}>
              {corrigoWorkOrders.length > 0 
                ? `Showing ${corrigoWorkOrders.length} completed work orders for favorited properties`
                : 'No completed work orders found for favorited properties. You can favorite properties by clicking the star icon on any property card.'
              }
            </div>
          </div>
        )}
        
        {/* Property Group Information */}
        {viewMode === 'propertyList' && selectedPropertyList && (
          <div className="d-flex justify-content-center mb-2">
            <div style={{
              padding: '8px 16px',
              backgroundColor: '#f0f9f1',
              border: '1px solid #c8e6c9',
              borderRadius: '4px',
              color: '#2e7d32',
              fontSize: '14px'
            }}>
              {corrigoWorkOrders.length > 0 
                ? `Showing ${corrigoWorkOrders.length} completed work orders for property list "${selectedPropertyList.name}"`
                : `No completed work orders found for property list "${selectedPropertyList.name}"`
              }
            </div>
          </div>
        )}
        
        {/* Date Range Picker */}
        <Card className="mb-4 shadow-sm">
          <Card.Body className="py-3">
            <Row className="align-items-center">
              <Col lg={6} md={12} className="d-flex align-items-center">
                <FaCalendarAlt className="text-primary mr-2" size={20} />
                <h5 className="font-weight-bold mb-0 mr-3">Date Range</h5>
                
                <div className="d-flex align-items-center flex-grow-1">
                  <div className="input-group mr-2">
                    <div className="input-group-prepend">
                      <span className="input-group-text">From</span>
                    </div>
                    <DatePicker
                      onChange={setTempStartDate}
                      value={tempStartDate}
                      className="form-control"
                      format="MM/dd/yyyy"
                      clearIcon={null}
                    />
                  </div>
                  
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <span className="input-group-text">To</span>
                    </div>
                    <DatePicker
                      onChange={setTempEndDate}
                      value={tempEndDate}
                      className="form-control"
                      format="MM/dd/yyyy"
                      clearIcon={null}
                    />
                  </div>
                </div>
              </Col>
              
              <Col lg={6} md={12} className="d-flex align-items-center mt-md-3 mt-lg-0">
                <Button 
                  variant="primary" 
                  onClick={applyDateRange}
                  className="mr-3"
                  disabled={isLoading}
                >
                  <FaFilter className="mr-1" /> Apply
                </Button>
                
                <div className="text-muted">
                  {isLoading ? (
                    <Spinner animation="border" size="sm" className="mr-2" />
                  ) : (
                    <>
                      <Badge variant="info" className="mr-2">
                        {corrigoWorkOrders.length} completed work orders
                      </Badge>
                      <small>
                        {moment(startDate).format('MMM DD, YYYY')} - {moment(endDate).format('MMM DD, YYYY')}
                      </small>
                    </>
                  )}
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>
        
        {error && (
          <Alert variant="danger" className="mb-4">
            <Alert.Heading>Error Loading Data</Alert.Heading>
            <p>{error}</p>
          </Alert>
        )}
        
        {updateMessage && (
          <Alert 
            variant={updateMessage.type} 
            dismissible 
            onClose={() => setUpdateMessage(null)}
            className="position-fixed"
            style={{ 
              top: '20px', 
              right: '20px', 
              zIndex: 1050,
              width: 'auto',
              maxWidth: '300px'
            }}
          >
            {updateMessage.text}
          </Alert>
        )}

        {/* Show empty state for favorited view when no results */}
        {viewMode === 'favorited' && corrigoWorkOrders.length === 0 && !isLoading && (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px 20px',
            color: '#6c757d',
            fontSize: '16px'
          }}>
            <div style={{ marginBottom: '10px' }}>
              🌟 No completed work orders found for your favorited properties!
            </div>
            <div style={{ fontSize: '14px' }}>
              {hasFavorites 
                ? 'Try adjusting the date range or switch back to "All Work Orders" to see more results.'
                : 'You can favorite properties by clicking the star icon on any property card in the Inspection page.'
              }
            </div>
          </div>
        )}
        
        {/* Show empty state for property list view when no results */}
        {viewMode === 'propertyList' && corrigoWorkOrders.length === 0 && !isLoading && selectedPropertyList && (
          <div style={{ 
            textAlign: 'center', 
            padding: '40px 20px',
            color: '#6c757d',
            fontSize: '16px'
          }}>
            <div style={{ marginBottom: '10px' }}>
              📋 No completed work orders found for property list "{selectedPropertyList.name}"!
            </div>
            <div style={{ fontSize: '14px' }}>
              Try adjusting the date range or switch to "All Work Orders" to see more results.
            </div>
          </div>
        )}
        
        {!isLoading && !error && (
          <>
            {/* Data Assumptions Card */}
            <Card className="mb-4 shadow-sm">
              <Card.Header className="bg-light d-flex justify-content-between align-items-center">
                <h5 className="mb-0 font-weight-bold">Data Assumptions</h5>
                <Button 
                  variant="success" 
                  size="sm"
                  onClick={handleSaveDataAssumptions}
                  disabled={isSaving}
                >
                  <FaSave className="mr-1" /> {isSaving ? 'Saving...' : 'Save Assumptions'}
                </Button>
              </Card.Header>
              <Card.Body>
                <Row>
                  <Col md={4}>
                    <Form.Group controlId="copelandDiscusCost">
                      <Form.Label className="font-weight-bold">Copeland Discus (Gross)</Form.Label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <span className="input-group-text">$</span>
                        </div>
                        <Form.Control
                          type="number"
                          value={copelandDiscusCost}
                          onChange={(e) => setCopelandDiscusCost(parseInt(e.target.value, 10) || 0)}
                        />
                      </div>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group controlId="carlyleShcCost">
                      <Form.Label className="font-weight-bold">Carlyle SHC (Gross)</Form.Label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <span className="input-group-text">$</span>
                        </div>
                        <Form.Control
                          type="number"
                          value={carlyleShcCost}
                          onChange={(e) => setCarlyleShcCost(parseInt(e.target.value, 10) || 0)}
                        />
                      </div>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Form.Group controlId="copelandScrollCost">
                      <Form.Label className="font-weight-bold">Copeland Scroll (Gross)</Form.Label>
                      <div className="input-group">
                        <div className="input-group-prepend">
                          <span className="input-group-text">$</span>
                        </div>
                        <Form.Control
                          type="number"
                          value={copelandScrollCost}
                          onChange={(e) => setCopelandScrollCost(parseInt(e.target.value, 10) || 0)}
                        />
                      </div>
                    </Form.Group>
                  </Col>
                </Row>
              </Card.Body>
            </Card>
            
            {/* Work Orders and Cost Avoidance Tables */}
            <Row>
              <Col md={12}>
                <Card className="mb-4 shadow-sm">
                  <Card.Header className="bg-light">
                    <h5 className="mb-0 font-weight-bold">Cost Avoidance Analysis</h5>
                  </Card.Header>
                  <Card.Body className="p-0">
                    <DataTable
                      columns={costAvoidanceColumns}
                      data={corrigoWorkOrders}
                      pagination
                      paginationPerPage={paginationPerPage}
                      paginationRowsPerPageOptions={[10, 25, 50, 100, 200]}
                      customStyles={tableCustomStyles}
                      noDataComponent={
                        <div style={{ padding: '24px', textAlign: 'center' }}>
                          <p>
                            {viewMode === 'favorited' 
                              ? 'No cost avoidance data available for favorited properties'
                              : viewMode === 'propertyList'
                              ? `No cost avoidance data available for property list "${selectedPropertyList?.name || 'selected'}"`
                              : 'No cost avoidance data available'
                            }
                          </p>
                        </div>
                      }
                      responsive
                      highlightOnHover
                      persistTableHead
                      defaultSortFieldId="dt_created"
                      defaultSortAsc={false}
                      striped
                    />
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          </>
        )}

        {isLoading && (
          <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: '300px' }}>
            <Spinner animation="border" role="status" style={{ color: '#007bff', width: '3rem', height: '3rem' }}>
              <span className="sr-only">Loading...</span>
            </Spinner>
            <p className="mt-3 text-muted">Loading work orders, please wait...</p>
          </div>
        )}
      </Container>
    </div>
  );
};

export default CostAvoidanceScreen;
